// Pricer-side handoff bridge (IM-1955). Two headless components:
//
//   HandoffTabOrchestrator — mounted once at the tab layer (inside
//     ExoticsTabsProvider). When a pendingIntent appears it upserts THIS
//     token's "Market Probabilities (<token>)" screen (keyed/named by the
//     display token, locked to the base_quote) and selects it. It does NOT
//     clear the intent — that's the seeder's job, so the tab is guaranteed
//     mounted before the seed lands.
//
//   HandoffSeeder — mounted inside each MP handoff tab's PricingProvider,
//     scoped to that tab's locked base_quote. It ACCUMULATES a basket: the
//     first handoff into a fresh screen REPLACES the default rows, and every
//     later click on the same screen APPENDS a new one-touch row — deduping an
//     identical rung and respecting the per-tab cap. After the mutation it
//     auto-calculates (only the new row on append; the whole basket on the
//     first seed) so the handoff arrives priced and the portfolio greeks
//     update. Rendered AFTER PricingTabContent so its seed effect runs after
//     the tab's default-seed effect.
import { FC, useContext, useEffect, useRef } from 'react';

import { PricerHandoffContext } from '../../providers/pricer-handoff/pricer-handoff-provider';
import { PricingContext } from '../../providers/exotics-pricing-provider';
import { ExoticsTabsContext } from '../../providers/exotics-tabs/exotics-tabs-provider';
import { handoffSpecToInstrumentSeed, isSameHandoffRung } from './handoff';
import { MAX_INSTRUMENTS } from './constants';

export const HandoffTabOrchestrator: FC = () => {
  const { pendingIntent } = useContext(PricerHandoffContext);
  const { openHandoffTab } = useContext(ExoticsTabsContext);
  useEffect(() => {
    if (!pendingIntent) return;
    openHandoffTab(pendingIntent.displayToken, pendingIntent.token);
  }, [pendingIntent, openHandoffTab]);
  return null;
};

export const HandoffSeeder: FC<{ token: string }> = ({ token }) => {
  const { pendingIntent, clearIntent } = useContext(PricerHandoffContext);
  const { instruments, addInstrument, setInstruments, calculate, calculateAll } =
    useContext(PricingContext);

  // Fresh instruments snapshot for the seed effect (which is NOT keyed on
  // instruments, so it fires once per intent rather than on every basket edit).
  const instrumentsRef = useRef(instruments);
  useEffect(() => {
    instrumentsRef.current = instruments;
  });

  // Whether this screen has already taken its first handoff seed. The first one
  // replaces the auto-seeded default rows; subsequent ones append.
  const hasSeededRef = useRef(false);
  // Deferred calculate target: an instrument id (calc just that new row) or
  // 'all' (calc the whole basket after the first-seed replace), consumed once
  // the mutation lands in state.
  const calcTargetRef = useRef<string | 'all' | null>(null);

  useEffect(() => {
    if (!pendingIntent || pendingIntent.token !== token) return;
    const spec = pendingIntent;
    const seed = handoffSpecToInstrumentSeed(spec);

    if (!hasSeededRef.current) {
      // First handoff into this screen — drop the blank defaults, start the
      // basket with this one-touch.
      setInstruments([seed]);
      hasSeededRef.current = true;
      calcTargetRef.current = 'all';
    } else {
      const existing = instrumentsRef.current;
      if (existing.some((i) => isSameHandoffRung(i, spec))) {
        // Identical rung already in the basket — dedupe (no-op).
      } else if (existing.length >= MAX_INSTRUMENTS) {
        // At the per-tab cap — no-op (the toolbar shows the at-capacity state).
      } else {
        calcTargetRef.current = addInstrument(seed);
      }
    }
    clearIntent();
  }, [pendingIntent, token, addInstrument, setInstruments, clearIntent]);

  // Fire the deferred calculate once the mutation is committed (so the target
  // exists in state / calculateAll's snapshot is fresh). Ref-gated to one fire;
  // a no-op if the WS isn't connected (calculateOne early-returns).
  useEffect(() => {
    const target = calcTargetRef.current;
    if (!target) return;
    if (target === 'all') {
      if (instruments.length === 0) return;
      calcTargetRef.current = null;
      calculateAll();
    } else {
      if (!instruments.some((i) => i.id === target)) return;
      calcTargetRef.current = null;
      calculate(target);
    }
  }, [instruments, calculate, calculateAll]);

  return null;
};
